import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import './GifSections.css'
import apiGETRequest from '../../../../../../../../utils/apiRequest';
import { networkService } from '../../../../../../../config/axios';
import Button from '../../../../../../../Elements/Button';
import { debounce } from '../../../../../../../../hooks/utils';

// Unsplash API guidelines require utm params on every link back to Unsplash.
const UTM = 'utm_source=kinetizine&utm_medium=referral';
const withUtm = (url) => (url ? `${url}${url.includes('?') ? '&' : '?'}${UTM}` : url);
const UNSPLASH_HOME = `https://unsplash.com/?${UTM}`;

// urls.regular is served 1080px wide (w=1080&fit=max, never upscaled) — that's the image we insert.
const REGULAR_WIDTH = 1080;
const insertedDimensions = ({ width, height }) => {
    if (!width || !height) return null;
    const w = Math.min(REGULAR_WIDTH, width);
    return { width: w, height: Math.round((height * w) / width) };
};

// Each user searches with their own Unsplash Access Key, saved under Profile → Integrations.
const PROFILE_KEY_URL = '/profile#unsplash-section';
const KEY_ERRORS = ['missing_key', 'invalid_key'];

// Editing an element placed from the retired Tenor picker still shows its image here.
const PICKER_GIF_TYPES = ['unsplash', 'tenor'];

const PhotoCredit = ({ name, link }) => (
    <div className="unsplash-credit">
        Photo by{' '}
        <a href={withUtm(link)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>{name}</a>
        {' '}on{' '}
        <a href={UNSPLASH_HOME} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>Unsplash</a>
    </div>
);

const UnsplashSection = ({ tool, handleSelectGifs, handleImageUpload, activeTab }) => {
    const [photos, setPhotos] = useState([]);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);
    // null | 'missing_key' | 'invalid_key' | 'rate_limited' | 'unavailable'
    const [loadError, setLoadError] = useState(null);
    const inputRef = useRef(null);

    const loadPhotos = async (searchQuery) => {
        setLoading(true);
        setLoadError(null);
        try {
            const response = await apiGETRequest(
                `editor/unsplash-photos?query=${encodeURIComponent(searchQuery.trim())}`
            );
            if (response?.status === 200) {
                setPhotos(response?.data ?? []);
            } else {
                setPhotos([]);
                setLoadError(response?.error || 'unavailable');
            }
        } catch (error) {
            console.error("Error fetching Unsplash photos:", error);
            setPhotos([]);
            setLoadError('unavailable');
        }
        setLoading(false);
    };

    const fetchPhotosDebounced = useCallback(
        debounce(async (searchQuery) => {
            await loadPhotos(searchQuery);
            setTimeout(() => inputRef.current?.focus(), 0);
        }, 1000),
        []
    );

    useEffect(() => {
        fetchPhotosDebounced(search);
    }, [search, fetchPhotosDebounced]);

    // The key is added in a new tab, so retry when the user comes back to the editor.
    const searchRef = useRef(search);
    searchRef.current = search;
    useEffect(() => {
        if (!KEY_ERRORS.includes(loadError)) return;
        const retry = () => loadPhotos(searchRef.current);
        window.addEventListener('focus', retry);
        return () => window.removeEventListener('focus', retry);
    }, [loadError]);

    useLayoutEffect(() => {
        setTimeout(() => inputRef.current?.focus(), 0);
    }, []);

    useEffect(() => {
        setTimeout(() => inputRef.current?.focus(), 0);
    }, [activeTab]);

    const selectPhoto = (photo) => {
        handleSelectGifs('unsplash', photo.url, { width: photo.width, height: photo.height }, {
            unsplash_id: photo.id,
            unsplash_user_name: photo.user_name,
            unsplash_user_link: photo.user_link,
        });
        // Required by Unsplash API terms; fire-and-forget so selection isn't blocked on it.
        if (photo.download_location) {
            networkService('editor/unsplash-photos/download', 'POST', { download_location: photo.download_location })
                .catch(() => {});
        }
        setPhotos([]);
    };

    const isEditingPickedImage = tool?.id && PICKER_GIF_TYPES.includes(tool?.gif_type);
    const showResults = !isEditingPickedImage || search?.length > 0;

    return (
        <div className='GIFs-main'>
            <div className="Gif-inp mb-2">
                <input
                    ref={inputRef}
                    placeholder="Search photos on Unsplash"
                    onChange={(e) => setSearch(e?.target?.value)}
                    value={search}
                    className='center-placeholder'
                    autoFocus={true}
                />
            </div>
            {showResults && loadError && !loading && (
                <div className='p-3 text-muted'>
                    {loadError === 'missing_key' && <>Your Unsplash API key is not added yet. </>}
                    {loadError === 'invalid_key' && <>Your Unsplash API key is no longer valid. </>}
                    {KEY_ERRORS.includes(loadError) && (
                        <a href={PROFILE_KEY_URL} target="_blank" rel="noopener noreferrer" className="unsplash-key-link">
                            {loadError === 'missing_key' ? 'Add it here' : 'Update it here'}
                        </a>
                    )}
                    {loadError === 'rate_limited' && "You've reached your Unsplash hourly request limit. Please try again later."}
                    {loadError === 'unavailable' && "Couldn't load photos from Unsplash. Please try again in a moment."}
                </div>
            )}
            {showResults && (loading || photos?.length > 0) ? (
                <div className='gifs-items'>
                    <div className='row flex-wrap gy-3'>
                        {loading ? <div className='p-3'>Loading......</div> :
                            photos.map((photo) => {
                                const dimensions = insertedDimensions(photo);
                                return (
                                    <div key={photo.id} className='col'>
                                        <div className='gif-item-wrapper' onClick={() => selectPhoto(photo)}>
                                            <img
                                                style={{ height: "160px", width: "160px", objectFit: "cover" }}
                                                src={photo.thumb}
                                                alt={photo.alt || 'Unsplash photo'}
                                            />
                                            {dimensions && (
                                                <div className="gif-dim-label">{dimensions.width} × {dimensions.height}px</div>
                                            )}
                                        </div>
                                        <PhotoCredit name={photo.user_name} link={photo.user_link} />
                                    </div>
                                );
                            })
                        }
                    </div>
                </div>
            ) : <></>}
            {PICKER_GIF_TYPES.includes(tool?.gif_type) && tool?.value?.gif_link && (
                <div className="d-flex align-items-start gap-2 mt-2">
                    <div>
                        <img
                            src={tool?.value?.gif_link}
                            alt='selected image'
                            style={{ height: "160px", width: "160px", objectFit: "cover", opacity: `${tool?.transparency ?? 10}` }}
                        />
                        {tool?.value?.unsplash_user_name && (
                            <PhotoCredit name={tool.value.unsplash_user_name} link={tool.value.unsplash_user_link} />
                        )}
                    </div>
                    <Button
                        text={<><i className="fa-regular fa-trash-can me-1" />Remove Image</>}
                        classList="danger-outline"
                        addonClass="mt-1"
                        onClick={(e) => { handleImageUpload(e, 'cancel'); loadPhotos(search); }}
                    />
                </div>
            )}
        </div>
    );
};

export default UnsplashSection;
